// BUG-523: a tab left open across a release keeps running the old JavaScript, because a single-page
// app never asks for index.html again. The deploy writes /version.json ({tag, sha, builtAt}, served
// no-store) and the build knows its own sha, so the app can ask "is a newer release live?" cheaply:
// when the tab is opened, when it comes back into view or gets focus, and every 5 minutes while it
// is visible. A hidden tab makes no requests. What to do about a new release is the caller's.

export const VERSION_CHECK_MS = 5 * 60 * 1000

const SHA = /^[0-9a-f]{7,40}$/

/** The sha of the release that is live now, or null when it cannot be told (offline, no file). */
export async function fetchLiveSha(fetchImpl: typeof fetch): Promise<string | null> {
  try {
    const res = await fetchImpl('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    // A missing file comes back as index.html with a 200 (the CDN's SPA fallback): json() throws.
    const body = (await res.json()) as unknown
    const sha = body && typeof body === 'object' ? (body as { sha?: unknown }).sha : undefined
    return typeof sha === 'string' && SHA.test(sha) ? sha : null
  } catch {
    return null
  }
}

export interface VersionWatcherOptions {
  /** The sha this bundle was built from. */
  buildSha: string
  /** Asks for the live sha (fetchLiveSha in the app). */
  check: () => Promise<string | null>
  /** Called with the live sha whenever it differs from buildSha. */
  onNewVersion: (sha: string) => void
  win: Pick<Window, 'addEventListener' | 'removeEventListener' | 'setInterval' | 'clearInterval'>
  doc: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>
  intervalMs?: number
}

/** Starts watching; returns stop(). */
export function createVersionWatcher(opts: VersionWatcherOptions): () => void {
  const { buildSha, check, onNewVersion, win, doc, intervalMs = VERSION_CHECK_MS } = opts
  let timer: number | null = null
  let inFlight = false
  let stopped = false

  async function poll() {
    if (inFlight || stopped) return
    inFlight = true
    try {
      const live = await check()
      if (!stopped && live && live !== buildSha) onNewVersion(live)
    } catch {
      // A failed check is retried on the next tick or focus.
    } finally {
      inFlight = false
    }
  }

  function onVisibility() {
    if (doc.visibilityState === 'visible') {
      if (timer === null) timer = win.setInterval(() => void poll(), intervalMs)
      void poll()
    } else if (timer !== null) {
      win.clearInterval(timer)
      timer = null
    }
  }
  const onFocus = () => void poll()

  doc.addEventListener('visibilitychange', onVisibility)
  win.addEventListener('focus', onFocus)
  onVisibility()

  return () => {
    stopped = true
    doc.removeEventListener('visibilitychange', onVisibility)
    win.removeEventListener('focus', onFocus)
    if (timer !== null) win.clearInterval(timer)
    timer = null
  }
}
